import { service } from '@ember/service';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import Chip from 'tio-ui/components/chip';
import type { ChipIntent } from 'tio-ui/components/chip';
import Component from '@glimmer/component';
import Text from 'tio-ui/components/text';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import { PhCheckCircle, PhWarning, PhCalendarCheck } from 'ember-phosphor-icons';
import type { AnnualAutoSubmitStatus } from 'tio-employee/utils/pslf/annual-auto-submit';
import type { IntlService } from 'ember-intl';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';
import { INACTIVE_STATUSES } from 'tio-data/data/resources/pslf-form.constants';

/** Panel / text / icon fingerprint per annual tier, so the strip cannot drift from the ring. */
const ANNUAL_TIER_STYLES = {
  quiet: {
    panel: 'app-status-panel--success',
    text: 'app-status-text-success',
    icon: 'success',
  },
  auto: { panel: 'app-status-panel--brand', text: 'app-status-text-brand', icon: 'auto' },
  warning: {
    panel: 'app-status-panel--warning',
    text: 'app-status-text-warning',
    icon: 'warning',
  },
  danger: { panel: 'app-status-panel--danger', text: 'app-status-text-danger', icon: 'danger' },
} as const;

function getStatusChipIntent(status: string): ChipIntent {
  switch (status) {
    case 'submitted':
    case 're_submitted':
    case 'submit_pending':
    case 're_submit_pending':
      return 'default';
    case 'borrower_signed':
      return 'warning';
    case 'employer_signed':
    case 'awaiting_submit':
      return 'primary';
    default:
      return 'default';
  }
}

function getStatusLabel(status: string, intl: IntlService): string {
  switch (status) {
    case 'submitted':
    case 'submit_pending':
    case 're_submitted':
    case 're_submit_pending':
      return intl.t('pslf.reimagined.form_status.status.pending_verification');
    case 'borrower_signed':
      return intl.t('pslf.reimagined.form_status.status.awaiting_employer');
    case 'employer_signed':
    case 'awaiting_submit':
      return intl.t('pslf.reimagined.form_status.status.awaiting_submit');
    default:
      return intl.t('pslf.reimagined.form_status.status.processing');
  }
}

function getStatusDescription(form: PslfFormModel, intl: IntlService): string {
  switch (form.status) {
    case 'borrower_signed':
      return intl.t('pslf.reimagined.form_status.current_employer.borrower_signed_description');
    case 'employer_signed':
    case 'awaiting_submit':
      return intl.t('pslf.reimagined.form_status.current_employer.employer_signed_description');
    default: {
      if (!form.submittedAt) {
        return intl.t('pslf.reimagined.form_status.status.pending_verification');
      }
      return intl.t('pslf.reimagined.form_status.current_employer.description', {
        date: intl.formatDate(new Date(form.submittedAt), {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        }),
      });
    }
  }
}

export interface FormStatusAlertsSignature {
  Args: {
    forms: PslfFormModel[];
    /** Omitted (or inactive) while `pslfAnnualAutoSubmit` is off, which keeps the pre-feature strip. */
    annualAutoSubmit?: AnnualAutoSubmitStatus;
  };
  Element: HTMLDivElement;
}

export default class PslfFormStatusAlerts extends Component<FormStatusAlertsSignature> {
  @service declare intl: IntlService;

  /**
   * Pick the single most relevant current employer form:
   * - Filter to currentEmployer only, exclude terminal statuses
   * - Sort by createdAt descending (newest first)
   * - Return the first one
   */
  get currentForm(): PslfFormModel | undefined {
    const candidates = this.args.forms
      .filter(
        (f) =>
          f.formType === 'currentEmployer' &&
          !(INACTIVE_STATUSES as readonly string[]).includes(f.status)
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return candidates[0];
  }

  get hasCurrentForm() {
    return !!this.currentForm;
  }

  /** True when the displayed form is expiring (submitted 11+ months ago). */
  get isExpiring() {
    return !!this.currentForm?.isExpiring;
  }

  /** A form still on its way to the DOE keeps this strip: next year's date beside "Awaiting Employer Signature" reads as a contradiction. */
  get annual() {
    const status = this.args.annualAutoSubmit;
    if (!status?.isActive || !this.currentForm?.isSubmitted) return undefined;
    return status;
  }

  get isAnnualManual() {
    const tier = this.annual?.tier;
    return tier === 'warning' || tier === 'danger';
  }

  get isAnnualUrgent() {
    return this.annual?.tier === 'danger';
  }

  get tierStyles() {
    if (this.annual) return ANNUAL_TIER_STYLES[this.annual.tier];
    return this.isExpiring ? ANNUAL_TIER_STYLES.warning : ANNUAL_TIER_STYLES.quiet;
  }

  get description() {
    if (this.annual) return this.annualDescription;
    if (this.isExpiring) {
      return this.intl.t('pslf.reimagined.form_status.current_employer.expiring_description');
    }
    const form = this.currentForm;
    return form ? getStatusDescription(form, this.intl) : '';
  }

  get annualDueDateLabel() {
    const due = this.annual?.dueDate;
    if (!due) return '';
    return this.intl.formatDate(due, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  get annualDescription() {
    const status = this.annual;
    if (!status) return '';

    const options = { date: this.annualDueDateLabel, days: status.daysRemaining };
    if (status.tier === 'quiet') {
      return this.intl.t('pslf.reimagined.annual_auto_submit.strip.quiet', options);
    }
    if (status.tier === 'auto') {
      return status.approverKind === 'employerSigns'
        ? this.intl.t('pslf.reimagined.annual_auto_submit.strip.auto_approver', options)
        : this.intl.t('pslf.reimagined.annual_auto_submit.strip.auto', options);
    }
    return status.tier === 'danger'
      ? this.intl.t('pslf.reimagined.annual_auto_submit.strip.urgent', options)
      : this.intl.t('pslf.reimagined.annual_auto_submit.strip.due_soon', options);
  }

  <template>
    {{! The surface stays inside the guard so an empty alert never occupies a slot in the page stack. }}
    {{#if this.hasCurrentForm}}
      {{#let this.currentForm as |form|}}
        {{#if form}}
          <Card
            @padding="none"
            class="overflow-hidden"
            data-test-current-employer-alert
            ...attributes
          >
            <div
              class="app-status-panel p-4 {{this.tierStyles.panel}}"
              data-test-annual-strip-tier={{if this.annual this.annual.tier}}
            >
              <div class="tio-col-tight">
                <div class="tio-row-tight flex-wrap">
                  {{#if (eq this.tierStyles.icon "success")}}
                    <PhCheckCircle @size={{20}} @weight="fill" class="app-icon-success shrink-0" />
                  {{else if (eq this.tierStyles.icon "auto")}}
                    <PhCalendarCheck @size={{20}} @weight="fill" class="tio-fg-brand shrink-0" />
                  {{else if (eq this.tierStyles.icon "danger")}}
                    <PhWarning @size={{20}} @weight="fill" class="app-icon-danger shrink-0" />
                  {{else}}
                    <PhWarning @size={{20}} @weight="fill" class="app-icon-warning shrink-0" />
                  {{/if}}
                  <Text @variant="inherit" @weight="semibold" class={{this.tierStyles.text}}>
                    {{t "pslf.reimagined.form_status.current_employer.heading"}}
                  </Text>
                  <Chip @intent={{getStatusChipIntent form.status}} @size="sm">
                    {{getStatusLabel form.status this.intl}}
                  </Chip>
                </div>
                <Text
                  @variant="inherit"
                  @size="sm"
                  class={{this.tierStyles.text}}
                  data-test-current-employer-alert-description
                >
                  {{this.description}}
                </Text>
                {{#if this.isAnnualManual}}
                  <div class="tio-row mt-1">
                    <Button
                      @appearance="outlined"
                      @intent={{if this.isAnnualUrgent "danger" "primary"}}
                      @size="sm"
                      @testHook="pslf-annual-strip__submit"
                      data-test-annual-strip-cta
                      {{on "click" (transitionTo "authenticated.pslf.dashboard.start-new")}}
                    >
                      {{#if this.isAnnualUrgent}}
                        {{t "pslf.reimagined.annual_auto_submit.cta_submit_now"}}
                      {{else}}
                        {{t "pslf.reimagined.annual_auto_submit.cta_submit_new"}}
                      {{/if}}
                    </Button>
                  </div>
                {{/if}}
              </div>
            </div>
          </Card>
        {{/if}}
      {{/let}}
    {{/if}}
  </template>
}
